import { useState } from "react";
import { fetchJson } from "../utils/http";
import { SERVER_URL } from "../../../constants";
import { pick } from "../utils/objects";
import { settleWithConcurrency } from "../utils/concurrency";

// Cap concurrent writes so a bulk "Save all" doesn't overrun the backend's
// JDBC connection pool (firing every PUT at once returned HTTP 500s).
const SAVE_CONCURRENCY = 4;

export function toDraft(raw) {
  return {
    id: pick(raw, "id"),
    dateAdded: pick(raw, "dateAdded", "date_added"),
    studentName: pick(raw, "studentName", "student_name"),
    studentSurname: pick(raw, "studentSurname", "student_surname"),
    studentDob: pick(raw, "studentDob", "student_dob"),
    studentGender: pick(raw, "studentGender", "student_gender"),
    studentNationality: pick(raw, "studentNationality", "student_nationality"),
    englishLevel: pick(raw, "englishLevel", "english_level"),
    allergies: pick(raw, "allergies"),
    notes: pick(raw, "notes"),
    hasPoolPermission: !!pick(raw, "hasPoolPermission", "has_pool_permission"),
    hasPhotoPermission: !!pick(raw, "hasPhotoPermission", "has_photo_permission"),
    hasMedicalPermission: !!pick(raw, "hasMedicalPermission", "has_medical_permission"),
    hasHospitalPermission: !!pick(raw, "hasHospitalPermission", "has_hospital_permission"),
    hasExcursionPermission: !!pick(raw, "hasExcursionPermission", "has_excursion_permission"),
    hasActivityPermission: !!pick(raw, "hasActivityPermission", "has_activity_permission"),
    hasSupervisionPermission: !!pick(raw, "hasSupervisionPermission", "has_supervision_permission"),
    emergencyContact: pick(raw, "emergencyContact", "emergency_contact"),
    mobilePhoneOptOut: !!pick(raw, "mobilePhoneOptOut", "mobile_phone_opt_out"),
    tshirtSize: pick(raw, "TshirtSize", "tshirtSize", "t_shirt_size"),
    mobileNumber: pick(raw, "mobileNumber", "mobile_number"),
  };
}
export function toPayload(d) {
  return {
    dateAdded: d.dateAdded,
    studentName: d.studentName ?? "",
    studentSurname: d.studentSurname ?? "",
    studentDob: d.studentDob || null,
    studentGender: d.studentGender ?? "",
    studentNationality: d.studentNationality ?? "",
    englishLevel: d.englishLevel ?? "",
    allergies: d.allergies ?? "",
    notes: d.notes ?? "",
    hasPoolPermission: !!d.hasPoolPermission,
    hasPhotoPermission: !!d.hasPhotoPermission,
    hasMedicalPermission: !!d.hasMedicalPermission,
    hasHospitalPermission: !!d.hasHospitalPermission,
    hasExcursionPermission: !!d.hasExcursionPermission,
    hasActivityPermission: !!d.hasActivityPermission,
    hasSupervisionPermission: !!d.hasSupervisionPermission,
    emergencyContact: d.emergencyContact ?? "",
    mobilePhoneOptOut: !!d.mobilePhoneOptOut,
    tshirtSize: d.tshirtSize ?? "",
    mobileNumber: d.mobileNumber ?? "",
  };
}

export function useEditableStudents({ token, onListPatched }) {
  const [editRows, setEditRows] = useState({});
  const [dirtyIds, setDirtyIds] = useState(new Set());

  const startEdit = (raw) => {
    const d = toDraft(raw);
    setEditRows((prev) => ({ ...prev, [d.id]: d }));
  };
  const cancelEdit = (id) => {
    setEditRows((prev) => { const copy = { ...prev }; delete copy[id]; return copy; });
    setDirtyIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
  };
  const changeField = (id, field, value) => {
    setEditRows((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
    setDirtyIds((prev) => new Set(prev).add(id));
  };
  const changeBool = (id, field, checked) => changeField(id, field, !!checked);

  const saveRow = async (id) => {
    const d = editRows[id]; if (!d) return;
    await fetchJson(`${SERVER_URL}api/students/${id}`, token, {
      method: "PUT", body: JSON.stringify(toPayload(d)),
    });
    cancelEdit(id);
    onListPatched?.((prev) => prev.map((s) => (s.id === id ? { ...s, ...d } : s)));
  };

  // Bulk save. Runs PUTs through a bounded pool and reports per-row outcomes
  // instead of crashing on the first rejection. Succeeded rows are cleared and
  // the list refreshed; failed rows are kept dirty + in edit mode so the user
  // can retry without re-keying. Never throws — returns { ok, saved, failed }.
  const saveAll = async () => {
    const ids = Array.from(dirtyIds);
    if (!ids.length) return { ok: true, saved: [], failed: [] };

    const results = await settleWithConcurrency(
      ids,
      (id) =>
        fetchJson(`${SERVER_URL}api/students/${id}`, token, {
          method: "PUT",
          body: JSON.stringify(toPayload(editRows[id])),
        }),
      SAVE_CONCURRENCY
    );

    const saved = [];
    const failed = [];
    for (const r of results) {
      if (r.status === "fulfilled") saved.push(r.item);
      else failed.push({ id: r.item, error: r.reason });
    }

    if (saved.length) {
      const savedSet = new Set(saved);
      // Clear only the rows that actually saved; keep failed drafts editable.
      setEditRows((prev) => {
        const next = { ...prev };
        saved.forEach((id) => delete next[id]);
        return next;
      });
      setDirtyIds((prev) => {
        const next = new Set(prev);
        saved.forEach((id) => next.delete(id));
        return next;
      });
      onListPatched?.((prev) =>
        prev.map((s) => (savedSet.has(s.id) && editRows[s.id] ? { ...s, ...editRows[s.id] } : s))
      );
    }

    if (failed.length) {
      console.error("[useEditableStudents.saveAll] failures:", failed);
    }

    return { ok: failed.length === 0, saved, failed };
  };

  return { editRows, dirtyIds, startEdit, cancelEdit, changeField, changeBool, saveRow, saveAll };
}
